import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useApp, type SheetState } from '../state/store';

const SNAP: Record<SheetState, number> = { peek: 0.14, half: 0.3, full: 0.92 };
const ORDER: SheetState[] = ['peek', 'half', 'full'];

type Props = { children: ReactNode };

function resolveHeight(state: SheetState, containerH: number): number {
  return Math.round(containerH * SNAP[state]);
}

export function BottomSheet({ children }: Props) {
  const sheetState = useApp((s) => s.sheetState);
  const setSheetState = useApp((s) => s.setSheetState);
  const ref = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<{ startY: number; startH: number } | null>(null);
  const [dragH, setDragH] = useState<number | null>(null);

  const containerH = ref.current?.parentElement?.clientHeight ?? window.innerHeight;
  const height = dragH ?? resolveHeight(sheetState, containerH);

  // Exponera sheetets höjd så att t.ex. kart-FAB:en kan följa med ovanför det.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--sheet-height', `${height}px`);
    root.style.setProperty('--sheet-anim', drag ? '0s' : '0.28s');
  }, [height, drag]);

  useEffect(() => {
    if (!drag) return;
    // Senaste höjden i en lokal variabel (inte dragH-state) så lyssnarna kan
    // registreras en gång per drag istället för att bytas ut på varje move.
    let lastH = drag.startH;
    const onMove = (e: PointerEvent): void => {
      const delta = drag.startY - e.clientY;
      lastH = Math.max(120, Math.min(containerH * 0.92, drag.startH + delta));
      setDragH(lastH);
    };
    // pointercancel (samtal, systemgest m.m.) måste också avsluta draget,
    // annars fastnar sheetet i drag-läge med fönsterlyssnarna kvar.
    const onEnd = (): void => {
      // Tryck utan drag: växla läge istället för att snäppa tillbaka till samma
      // (peek/halv → större, full → halv). Gör grippen användbar även utan svep.
      if (Math.abs(lastH - drag.startH) < 8) {
        const next: Record<SheetState, SheetState> = { peek: 'half', half: 'full', full: 'half' };
        setSheetState(next[useApp.getState().sheetState]);
        setDrag(null);
        setDragH(null);
        return;
      }
      let best: SheetState = 'half';
      let bestDist = Infinity;
      for (const s of ORDER) {
        const d = Math.abs(resolveHeight(s, containerH) - lastH);
        if (d < bestDist) {
          bestDist = d;
          best = s;
        }
      }
      setSheetState(best);
      setDrag(null);
      setDragH(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onEnd);
    window.addEventListener('pointercancel', onEnd);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onEnd);
      window.removeEventListener('pointercancel', onEnd);
    };
  }, [drag, containerH, setSheetState]);

  return (
    <div
      ref={ref}
      className={`bottom-sheet${drag ? ' bottom-sheet--dragging' : ''}`}
      style={{ height }}
    >
      <button
        type="button"
        className="sheet-grip"
        aria-label="Dra eller tryck för att ändra storlek"
        onPointerDown={(e) => setDrag({ startY: e.clientY, startH: height })}
      >
        <span className="sheet-grip-bar" />
      </button>
      <div className="sheet-body">{children}</div>
    </div>
  );
}
